import { PlusIcon } from '@shopify/polaris-icons';
import { Page, TextField, Modal, Select } from '../components/forms';
import { useEffect, useState } from 'react';
import {
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  Button,
  DataTable,
  Spinner,
  Banner,
  Checkbox,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';

interface DiscountCode {
  id: number;
  code: string;
  description: string | null;
  discount_percent: number;
  discount_months: number;
  valid_from: string;
  valid_until: string | null;
  max_uses: number | null;
  times_used: number;
  applies_to_plans: string;
  created_by: string;
  created_at: string;
  is_active: number;
}

interface Plan {
  code: string;
  name: string;
}

export default function DiscountCodes() {
  const { fetchApi, error } = useApi();
  const [codes, setCodes] = useState<DiscountCode[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCode, setEditingCode] = useState<DiscountCode | null>(null);

  // Form state
  const [formCode, setFormCode] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formDiscountPercent, setFormDiscountPercent] = useState('');
  const [formDiscountMonths, setFormDiscountMonths] = useState('0');
  const [formValidUntil, setFormValidUntil] = useState('');
  const [formMaxUses, setFormMaxUses] = useState('');
  const [formSelectedPlans, setFormSelectedPlans] = useState<string[]>([]);
  const [formIsActive, setFormIsActive] = useState(true);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    loadDiscountCodes();
    loadPlans();
  }, []);

  async function loadDiscountCodes() {
    const response = await fetchApi<{ success: boolean; codes: DiscountCode[] }>('/super-admin/discount-codes');
    if (response?.success) {
      setCodes(response.codes);
    }
    setLoading(false);
  }

  async function loadPlans() {
    const response = await fetchApi<{ success: boolean; plans: Plan[] }>('/super-admin/plans');
    if (response?.success) {
      setPlans(response.plans);
    }
  }

  function openCreateModal() {
    setEditingCode(null);
    setFormCode('');
    setFormDescription('');
    setFormDiscountPercent('');
    setFormDiscountMonths('0');
    setFormValidUntil('');
    setFormMaxUses('');
    setFormSelectedPlans([]);
    setFormIsActive(true);
    setSaveError(null);
    setModalOpen(true);
  }

  function openEditModal(code: DiscountCode) {
    setEditingCode(code);
    setFormCode(code.code);
    setFormDescription(code.description || '');
    setFormDiscountPercent(code.discount_percent.toString());
    setFormDiscountMonths(code.discount_months.toString());
    setFormValidUntil(code.valid_until ? code.valid_until.split('T')[0] : '');
    setFormMaxUses(code.max_uses?.toString() || '');
    try {
      const planList = JSON.parse(code.applies_to_plans || '[]');
      setFormSelectedPlans(Array.isArray(planList) ? planList : []);
    } catch {
      setFormSelectedPlans([]);
    }
    setFormIsActive(code.is_active === 1);
    setSaveError(null);
    setModalOpen(true);
  }

  async function handleSave() {
    if (!formCode.trim()) {
      setSaveError('Code is required');
      return;
    }
    if (!formDiscountPercent || parseInt(formDiscountPercent, 10) <= 0) {
      setSaveError('Discount percent must be greater than 0');
      return;
    }

    setSaving(true);
    setSaveError(null);

    const payload = {
      code: formCode.trim().toUpperCase(),
      description: formDescription.trim() || null,
      discount_percent: parseInt(formDiscountPercent, 10),
      discount_months: parseInt(formDiscountMonths, 10) || 0,
      valid_until: formValidUntil || null,
      max_uses: formMaxUses ? parseInt(formMaxUses, 10) : null,
      applies_to_plans: formSelectedPlans,
      is_active: formIsActive,
    };

    let response;
    if (editingCode) {
      response = await fetchApi<{ success: boolean; error?: string }>(
        `/super-admin/discount-codes/${editingCode.id}`,
        {
          method: 'PUT',
          body: JSON.stringify(payload),
        }
      );
    } else {
      response = await fetchApi<{ success: boolean; error?: string }>(
        '/super-admin/discount-codes',
        {
          method: 'POST',
          body: JSON.stringify(payload),
        }
      );
    }

    setSaving(false);

    if (response?.success) {
      setModalOpen(false);
      loadDiscountCodes();
    } else {
      setSaveError(response?.error || 'Failed to save discount code');
    }
  }

  async function handleDelete(code: DiscountCode) {
    if (!confirm(`Delete discount code ${code.code}? This cannot be undone.`)) {
      return;
    }

    const response = await fetchApi<{ success: boolean; error?: string }>(
      `/super-admin/discount-codes/${code.id}`,
      { method: 'DELETE' }
    );

    if (response?.success) {
      loadDiscountCodes();
    } else {
      alert(response?.error || 'Failed to delete discount code');
    }
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IE');
  };

  const togglePlanSelection = (planCode: string) => {
    setFormSelectedPlans(prev =>
      prev.includes(planCode)
        ? prev.filter(p => p !== planCode)
        : [...prev, planCode]
    );
  };

  const rows = codes.map((code) => {
    let plansApply = 'All plans';
    try {
      const planList = JSON.parse(code.applies_to_plans || '[]');
      if (Array.isArray(planList) && planList.length > 0) {
        plansApply = planList.join(', ');
      }
    } catch {
      // Keep default
    }

    return [
      <div key={code.id}>
        <Text as="span" fontWeight="semibold">{code.code}</Text>
        {code.description && (
          <>
            <br />
            <Text as="span" tone="subdued" variant="bodySm">{code.description}</Text>
          </>
        )}
      </div>,
      <Badge key={`pct-${code.id}`} tone="success">{`${code.discount_percent}% off`}</Badge>,
      code.discount_months > 0 ? `${code.discount_months} months` : 'Forever',
      <div key={`uses-${code.id}`}>
        <Text as="span">{code.times_used}</Text>
        {code.max_uses && <Text as="span" tone="subdued"> / {code.max_uses}</Text>}
      </div>,
      formatDate(code.valid_until),
      plansApply,
      code.is_active ? (
        <Badge key={`status-${code.id}`} tone="success">Active</Badge>
      ) : (
        <Badge key={`status-${code.id}`} tone="critical">Inactive</Badge>
      ),
      <InlineStack key={`actions-${code.id}`} gap="200">
        <Button size="slim" onClick={() => openEditModal(code)}>Edit</Button>
        <Button size="slim" tone="critical" onClick={() => handleDelete(code)}>Delete</Button>
      </InlineStack>,
    ];
  });

  return (
    <Page
      title="Discount Codes"
      primaryAction={{
        icon: PlusIcon, content: 'Create Code',
        onAction: openCreateModal,
      }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <BlockStack gap="200">
                <Text as="h2" variant="headingMd">About Discount Codes</Text>
                <Text as="p" tone="subdued">
                  Create discount codes that merchants can use during signup to get a percentage
                  discount on their subscription. Codes can have expiry dates, usage limits, and
                  can be restricted to specific plans.
                </Text>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            {loading ? (
              <InlineStack align="center">
                <Spinner size="large" />
              </InlineStack>
            ) : codes.length === 0 ? (
              <BlockStack gap="200" inlineAlign="center">
                <Text as="p" tone="subdued">No discount codes created yet</Text>
                <Button icon={PlusIcon} onClick={openCreateModal}>Create your first code</Button>
              </BlockStack>
            ) : (
              <DataTable
                columnContentTypes={['text', 'text', 'text', 'numeric', 'text', 'text', 'text', 'text']}
                headings={['Code', 'Discount', 'Duration', 'Uses', 'Expires', 'Plans', 'Status', 'Actions']}
                rows={rows}
              />
            )}
          </Card>
        </Layout.Section>
      </Layout>

      {/* Create/Edit Modal */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingCode ? `Edit Code: ${editingCode.code}` : 'Create Discount Code'}
        primaryAction={{
          icon: editingCode ? undefined : PlusIcon, content: editingCode ? 'Save Changes' : 'Create Code',
          onAction: handleSave,
          loading: saving,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setModalOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {saveError && (
              <Banner tone="critical">{saveError}</Banner>
            )}

            <TextField
              label="Code"
              requiredIndicator value={formCode}
              onChange={setFormCode}
              placeholder="WELCOME20"
              helpText="Uppercase, no spaces. This is what merchants will enter."
              autoComplete="off"
              disabled={!!editingCode}
            />

            <TextField
              label="Description (optional)"
              value={formDescription}
              onChange={setFormDescription}
              placeholder="Welcome offer for new merchants"
              autoComplete="off"
            />

            <InlineStack gap="400" wrap={false}>
              <div style={{ flex: 1 }}>
                <TextField
                  label="Discount Percent"
                  type="number"
                  requiredIndicator value={formDiscountPercent}
                  onChange={setFormDiscountPercent}
                  placeholder="20"
                  suffix="%"
                  autoComplete="off"
                />
              </div>
              <div style={{ flex: 1 }}>
                <Select
                  label="Duration"
                  options={[
                    { label: 'Forever', value: '0' },
                    { label: '1 month', value: '1' },
                    { label: '3 months', value: '3' },
                    { label: '6 months', value: '6' },
                    { label: '12 months', value: '12' },
                  ]}
                  value={formDiscountMonths}
                  onChange={setFormDiscountMonths}
                  helpText="How long the discount applies"
                />
              </div>
            </InlineStack>

            <InlineStack gap="400" wrap={false}>
              <div style={{ flex: 1 }}>
                <TextField
                  label="Expires (optional)"
                  type="date"
                  value={formValidUntil}
                  onChange={setFormValidUntil}
                  autoComplete="off"
                  helpText="Leave empty for no expiry"
                />
              </div>
              <div style={{ flex: 1 }}>
                <TextField
                  label="Max Uses (optional)"
                  type="number"
                  value={formMaxUses}
                  onChange={setFormMaxUses}
                  placeholder="100"
                  autoComplete="off"
                  helpText="Leave empty for unlimited"
                />
              </div>
            </InlineStack>

            <BlockStack gap="200">
              <Text as="span" variant="bodyMd" fontWeight="semibold">Applies to Plans</Text>
              <Text as="p" tone="subdued" variant="bodySm">
                Select which plans this code can be used with. Leave all unchecked for all plans.
              </Text>
              <BlockStack gap="100">
                {plans.map((plan) => (
                  <Checkbox
                    key={plan.code}
                    label={plan.name}
                    checked={formSelectedPlans.includes(plan.code)}
                    onChange={() => togglePlanSelection(plan.code)}
                  />
                ))}
              </BlockStack>
            </BlockStack>

            {editingCode && (
              <Checkbox
                label="Active"
                helpText="Inactive codes cannot be used"
                checked={formIsActive}
                onChange={setFormIsActive}
              />
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
